<script setup>
import { ref, computed, onMounted } from 'vue'
import { subscribeClients, updateClient } from '../../../core/services/clients.js'
import { subscribeUserProfiles } from '../../../core/services/users.js'
import { formatDateTime } from '@core/utils/formatting.js'
import { getClientStatusClass } from '@core/utils/statusHelpers.js'
import { getMarketingState, hasAcceptedSiteTerms } from '@core/utils/consent.js'
import ClientConsentPanel from '@ui/components/admin/clients/ClientConsentPanel.vue'
import { usePagination } from '@core/composables/usePagination.js'
import { useFirestoreSubscription } from '@core/composables/useFirestoreSubscription.js'
import { useToast } from '@core/composables/useToast.js'

const { track } = useFirestoreSubscription()
const { success: toastSuccess, error: toastError } = useToast()

// ============================================
// State
// ============================================
const clients = ref([])
const isLoading = ref(true)
const error = ref(null)

// User profiles for linked status check
const profilesByUid = ref({})

// Filters (instant - no apply button)
const searchQuery = ref('')
const statusFilter = ref('')
const agentFilter = ref('')
const cityFilter = ref('')
const linkedFilter = ref('') // '', 'linked', 'not-linked'
const visitDayFilter = ref('')
// Default open on desktop (>768px), closed on mobile
const filtersExpanded = ref(window.innerWidth > 768)

// Expand/collapse state
const expandedRows = ref(new Set())

// Full-screen modal state
const modalClientId = ref(null)
// Live doc from the realtime `clients` list (so consent edits inside the modal re-render)
const modalClient = computed(() =>
  modalClientId.value ? clients.value.find((c) => c.id === modalClientId.value) || null : null
)

// ============================================
// Realtime subscriptions
// ============================================
onMounted(() => {
  // Subscribe to ClientsV2
  track(
    subscribeClients((data, err) => {
      if (err) {
        console.error('Clients subscription error:', err)
        error.value = err.message || 'שגיאה בטעינת לקוחות'
      } else {
        clients.value = data
        error.value = null
      }
      isLoading.value = false
    })
  )

  // Subscribe to Users for linked status
  track(
    subscribeUserProfiles((profiles, err) => {
      if (err) {
        console.error('Profiles subscription error:', err)
      } else {
        profilesByUid.value = profiles
      }
    })
  )
})

// ============================================
// Computed: Linked makats set
// ============================================
const linkedMakats = computed(() => {
  const set = new Set()
  Object.values(profilesByUid.value).forEach((profile) => {
    if (profile.client_makat) {
      set.add(profile.client_makat)
    }
  })
  return set
})

// ============================================
// Computed: Unique values for filter dropdowns
// ============================================
const uniqueStatuses = computed(() => {
  const statuses = new Set()
  clients.value.forEach((c) => {
    if (c.status) statuses.add(c.status)
  })
  return Array.from(statuses).sort()
})

const uniqueCities = computed(() => {
  const cities = new Set()
  clients.value.forEach((c) => {
    if (c.city) cities.add(c.city)
  })
  return Array.from(cities).sort()
})

const uniqueVisitDays = computed(() => {
  const days = new Set()
  clients.value.forEach((c) => {
    if (c.visitation?.DAYNAME) days.add(c.visitation.DAYNAME)
  })
  const dayOrder = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת']
  return Array.from(days).sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
})

// ============================================
// Filtering (instant)
// ============================================
const filteredClients = computed(() => {
  let result = clients.value

  // Search filter (instant, case-insensitive)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.trim().toLowerCase()
    result = result.filter((client) => {
      const name = (client.name || '').toLowerCase()
      const makat = (client.makat || '').toLowerCase()
      const phone = (client.phone || '').toLowerCase()
      const city = (client.city || '').toLowerCase()
      const agent = String(client.agent || '').toLowerCase()
      const status = (client.status || '').toLowerCase()
      const vat = (client.vat || '').toLowerCase()
      const payment = (client.payment || '').toLowerCase()
      const priceListId = String(client.price_list_id || '').toLowerCase()
      return (
        name.includes(query) ||
        makat.includes(query) ||
        phone.includes(query) ||
        city.includes(query) ||
        agent.includes(query) ||
        status.includes(query) ||
        vat.includes(query) ||
        payment.includes(query) ||
        priceListId.includes(query)
      )
    })
  }

  // Status filter
  if (statusFilter.value) {
    result = result.filter((c) => c.status === statusFilter.value)
  }

  // Agent filter (numeric)
  if (agentFilter.value !== '') {
    const agentNum = Number(agentFilter.value)
    if (!isNaN(agentNum)) {
      result = result.filter((c) => Number(c.agent) === agentNum)
    }
  }

  // City filter
  if (cityFilter.value) {
    result = result.filter((c) => c.city === cityFilter.value)
  }

  // Linked filter
  if (linkedFilter.value === 'linked') {
    result = result.filter((c) => linkedMakats.value.has(c.makat))
  } else if (linkedFilter.value === 'not-linked') {
    result = result.filter((c) => !linkedMakats.value.has(c.makat))
  }

  // Visit day filter
  if (visitDayFilter.value) {
    result = result.filter((c) => c.visitation?.DAYNAME === visitDayFilter.value)
  }

  return result
})

// ============================================
// Pagination
// ============================================
const {
  currentPage,
  pageSize,
  totalPages,
  paginatedItems: paginatedClients,
  pageSizeOptions,
  resetPagination: resetPage,
} = usePagination(filteredClients, {
  defaultPageSize: 25,
  pageSizeOptions: [10, 25, 50, 100],
})

// ============================================
// Expand/Collapse
// ============================================
function toggleExpand(id) {
  if (expandedRows.value.has(id)) {
    expandedRows.value.delete(id)
  } else {
    expandedRows.value.add(id)
  }
  // Trigger reactivity
  expandedRows.value = new Set(expandedRows.value)
}

function isExpanded(id) {
  return expandedRows.value.has(id)
}

// ============================================
// Full-screen Modal
// ============================================
function openClientModal(client) {
  modalClientId.value = client.id
  document.body.style.overflow = 'hidden'
}

function closeClientModal() {
  modalClientId.value = null
  document.body.style.overflow = ''
}

// ============================================
// Filters toggle
// ============================================
function toggleFilters() {
  filtersExpanded.value = !filtersExpanded.value
}

const activeFiltersCount = computed(() => {
  let count = 0
  if (searchQuery.value.trim()) count++
  if (statusFilter.value) count++
  if (agentFilter.value !== '') count++
  if (cityFilter.value) count++
  if (linkedFilter.value) count++
  if (visitDayFilter.value) count++
  return count
})

function clearFilters() {
  searchQuery.value = ''
  statusFilter.value = ''
  agentFilter.value = ''
  cityFilter.value = ''
  linkedFilter.value = ''
  visitDayFilter.value = ''
  resetPage()
}

// ============================================
// Helpers
// ============================================
function formatValue(val) {
  if (val === null || val === undefined || val === '') return '—'
  return val
}

function isClientLinked(makat) {
  return linkedMakats.value.has(makat)
}

// Marketing consent badge (nested `marketing` or legacy `tos` — see consent.js)
const MARKETING_LABELS = { approved: 'מאושר', declined: 'לא', pending: 'טרם' }
const MARKETING_CLASSES = {
  approved: 'tos-approved',
  declined: 'tos-declined',
  pending: 'tos-pending',
}

function getTosLabel(client) {
  return MARKETING_LABELS[getMarketingState(client)]
}

function getTosClass(client) {
  return MARKETING_CLASSES[getMarketingState(client)]
}

function getTermsLabel(client) {
  return hasAcceptedSiteTerms(client) ? 'מאושר' : 'טרם'
}

function getTermsClass(client) {
  return hasAcceptedSiteTerms(client) ? 'tos-approved' : 'tos-pending'
}

// ============================================
// Toggle Prices
// ============================================
const updatingPrices = ref(new Set())

async function togglePrices(client) {
  if (updatingPrices.value.has(client.id)) return // Prevent double-click

  updatingPrices.value.add(client.id)
  updatingPrices.value = new Set(updatingPrices.value) // Trigger reactivity

  try {
    const newValue = !client.prices
    await updateClient(client.id, { prices: newValue })
    // Data will update via onSnapshot subscription
  } catch (err) {
    console.error('Failed to update prices:', err)
    alert('שגיאה בעדכון מחירים: ' + (err.message || 'שגיאה לא ידועה'))
  } finally {
    updatingPrices.value.delete(client.id)
    updatingPrices.value = new Set(updatingPrices.value) // Trigger reactivity
  }
}

function isUpdatingPrices(clientId) {
  return updatingPrices.value.has(clientId)
}

// ============================================
// First Order (first_order) toggle
// ============================================
const updatingFirstOrder = ref(new Set())
const firstOrderConfirm = ref(null) // { client, newValue }

function requestFirstOrderChange(client, event) {
  const selectEl = event.target
  const newValue = selectEl.value === 'true'
  if (newValue === !!client.first_order) return
  firstOrderConfirm.value = { client, newValue, selectEl }
}

async function confirmFirstOrderChange() {
  const { client, newValue } = firstOrderConfirm.value
  firstOrderConfirm.value = null

  updatingFirstOrder.value.add(client.id)
  updatingFirstOrder.value = new Set(updatingFirstOrder.value)

  try {
    await updateClient(client.id, { first_order: newValue })
    toastSuccess(`זכאות למבצע ראשון עודכנה ל"${newValue ? 'זכאי' : 'לא זכאי'}" עבור ${client.name}`)
  } catch (err) {
    console.error('Failed to update first_order:', err)
    toastError('שגיאה בעדכון זכאות למבצע ראשון: ' + (err.message || 'שגיאה לא ידועה'))
  } finally {
    updatingFirstOrder.value.delete(client.id)
    updatingFirstOrder.value = new Set(updatingFirstOrder.value)
  }
}

function cancelFirstOrderChange() {
  if (firstOrderConfirm.value?.selectEl) {
    const { client, selectEl } = firstOrderConfirm.value
    selectEl.value = client.first_order ? 'true' : 'false'
  }
  firstOrderConfirm.value = null
}

function isUpdatingFirstOrder(clientId) {
  return updatingFirstOrder.value.has(clientId)
}

// ============================================
// Min Order (min_order) inline edit
// ============================================
const updatingMinOrder = ref(new Set())
const minOrderDrafts = ref({})

function minOrderInputValue(client) {
  const draft = minOrderDrafts.value[client.id]
  return draft !== undefined ? draft : String(Number(client.min_order) || 0)
}

function onMinOrderInput(client, event) {
  minOrderDrafts.value = { ...minOrderDrafts.value, [client.id]: event.target.value }
}

function isMinOrderDirty(client) {
  const draft = minOrderDrafts.value[client.id]
  if (draft === undefined || draft === '') return false
  return Number(draft) !== (Number(client.min_order) || 0)
}

async function saveMinOrder(client) {
  if (updatingMinOrder.value.has(client.id)) return
  if (!isMinOrderDirty(client)) return

  const value = Number(minOrderDrafts.value[client.id])
  if (!Number.isFinite(value) || value < 0) {
    toastError('מינימום הזמנה חייב להיות מספר 0 או גדול ממנו')
    return
  }

  updatingMinOrder.value.add(client.id)
  updatingMinOrder.value = new Set(updatingMinOrder.value)

  try {
    await updateClient(client.id, { min_order: value })
    const { [client.id]: _removed, ...rest } = minOrderDrafts.value
    minOrderDrafts.value = rest
    toastSuccess(
      value > 0
        ? `מינימום הזמנה עודכן ל-${value} ₪ עבור ${client.name}`
        : `מינימום ההזמנה בוטל עבור ${client.name}`
    )
  } catch (err) {
    console.error('Failed to update min_order:', err)
    toastError('שגיאה בעדכון מינימום הזמנה: ' + (err.message || 'שגיאה לא ידועה'))
  } finally {
    updatingMinOrder.value.delete(client.id)
    updatingMinOrder.value = new Set(updatingMinOrder.value)
  }
}

function isUpdatingMinOrder(clientId) {
  return updatingMinOrder.value.has(clientId)
}

// ============================================
// Visitation Calendar
// ============================================
const hebrewDays = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳']

function parseVisitation(visitation) {
  if (!visitation || typeof visitation !== 'object') return null
  // visitation is a map with DAYNUM, WEEK1..WEEK4
  const dayNum = Number(visitation.DAYNUM)
  if (isNaN(dayNum) || dayNum < 1 || dayNum > 7) return null

  const weeks = [
    visitation.WEEK1 === 'Y',
    visitation.WEEK2 === 'Y',
    visitation.WEEK3 === 'Y',
    visitation.WEEK4 === 'Y',
  ]

  return { dayNum, weeks }
}
</script>

<template>
  <div class="admin-clients">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">ניהול לקוחות</h1>
      <p class="page-subtitle">צפייה בנתוני לקוחות מ-ClientsV2</p>
    </div>

    <!-- Actions Bar -->
    <div class="actions-bar">
      <div class="actions-right">
        <button
          class="btn-filters-toggle"
          :class="{ active: filtersExpanded }"
          @click="toggleFilters"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <span>סינון</span>
          <span v-if="activeFiltersCount > 0" class="filter-badge">{{ activeFiltersCount }}</span>
        </button>
      </div>
      <div class="actions-left">
        <span class="results-count">{{ filteredClients.length }} לקוחות</span>
      </div>
    </div>

    <!-- Filters Section (collapsible) -->
    <Transition name="filters-collapse">
      <div v-show="filtersExpanded" class="filters-section">
        <div class="filters-bar">
          <!-- Search -->
          <div class="search-input-container">
            <svg
              class="search-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="חיפוש לפי שם, מק״ט, טלפון, עיר..."
              @input="resetPage"
            />
          </div>

          <!-- Status Filter -->
          <div class="filter-group">
            <label class="filter-label">סטטוס</label>
            <div class="select-wrapper">
              <select v-model="statusFilter" class="filter-select" @change="resetPage">
                <option value="">הכל</option>
                <option v-for="status in uniqueStatuses" :key="status" :value="status">
                  {{ status }}
                </option>
              </select>
            </div>
          </div>

          <!-- Agent Filter -->
          <div class="filter-group">
            <label class="filter-label">סוכן</label>
            <input
              v-model="agentFilter"
              type="number"
              class="filter-input"
              placeholder="מספר סוכן"
              min="0"
              max="999"
              @input="resetPage"
            />
          </div>

          <!-- City Filter -->
          <div class="filter-group">
            <label class="filter-label">עיר</label>
            <div class="select-wrapper">
              <select v-model="cityFilter" class="filter-select" @change="resetPage">
                <option value="">הכל</option>
                <option v-for="city in uniqueCities" :key="city" :value="city">{{ city }}</option>
              </select>
            </div>
          </div>

          <!-- Visit Day Filter -->
          <div class="filter-group">
            <label class="filter-label">יום ביקור</label>
            <div class="select-wrapper">
              <select v-model="visitDayFilter" class="filter-select" @change="resetPage">
                <option value="">הכל</option>
                <option v-for="day in uniqueVisitDays" :key="day" :value="day">{{ day }}</option>
              </select>
            </div>
          </div>

          <!-- Linked Filter -->
          <div class="filter-group">
            <label class="filter-label">קישור למשתמש</label>
            <div class="select-wrapper">
              <select v-model="linkedFilter" class="filter-select" @change="resetPage">
                <option value="">הכל</option>
                <option value="linked">מקושר</option>
                <option value="not-linked">לא מקושר</option>
              </select>
            </div>
          </div>

          <!-- Clear Filters -->
          <button v-if="activeFiltersCount > 0" class="btn-clear-filters" @click="clearFilters">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            נקה סינון
          </button>
        </div>
      </div>
    </Transition>

    <!-- Content Section -->
    <div class="content-section">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container">
        <div class="spinner"></div>
        <span class="loading-text">טוען לקוחות...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <svg
          viewBox="0 0 24 24"
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p class="error-text">{{ error }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredClients.length === 0" class="empty-container">
        <svg
          viewBox="0 0 24 24"
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
        <p class="empty-text">לא נמצאו לקוחות</p>
        <p v-if="activeFiltersCount > 0" class="empty-hint">נסה לשנות את הסינון</p>
      </div>

      <!-- Table (Desktop) -->
      <div v-else class="table-container desktop-only">
        <table class="data-table">
          <thead>
            <tr>
              <th class="th-expand"></th>
              <th class="th-name">שם</th>
              <th class="th-makat">מק״ט</th>
              <th class="th-agent">סוכן</th>
              <th class="th-phone">טלפון</th>
              <th class="th-city">עיר</th>
              <th class="th-visit-day">יום ביקור</th>
              <th class="th-price-list">מחירון</th>
              <th class="th-linked">קישור</th>
              <th class="th-tos">שיווק</th>
              <th class="th-tos">תקנון</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="client in paginatedClients" :key="client.id">
              <tr
                class="data-row"
                :class="{ expanded: isExpanded(client.id) }"
                @click="toggleExpand(client.id)"
              >
                <td class="td-expand">
                  <div class="expand-actions">
                    <button
                      class="expand-btn"
                      @click.stop="toggleExpand(client.id)"
                      title="הרחב/צמצם"
                    >
                      <svg
                        :class="{ rotated: isExpanded(client.id) }"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    <button
                      class="fullscreen-btn"
                      @click.stop="openClientModal(client)"
                      title="תצוגה מלאה"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="td-name">
                  <span class="cell-value cell-name">{{ formatValue(client.name) }}</span>
                </td>
                <td class="td-makat">
                  <span class="cell-value cell-makat">{{ formatValue(client.makat) }}</span>
                </td>
                <td class="td-agent">
                  <span class="cell-value">{{ formatValue(client.agent) }}</span>
                </td>
                <td class="td-phone">
                  <span class="cell-value">{{ formatValue(client.phone) }}</span>
                </td>
                <td class="td-city">
                  <span class="cell-value">{{ formatValue(client.city) }}</span>
                </td>
                <td class="td-visit-day">
                  <span class="cell-value">{{ client.visitation?.DAYNAME || '—' }}</span>
                </td>
                <td class="td-price-list">
                  <span class="cell-value">{{ formatValue(client.price_list_id) }}</span>
                </td>
                <td class="td-linked">
                  <span v-if="isClientLinked(client.makat)" class="linked-badge linked">מקושר</span>
                  <span v-else class="linked-badge not-linked">—</span>
                </td>
                <td class="td-tos">
                  <span class="tos-badge" :class="getTosClass(client)">
                    {{ getTosLabel(client) }}
                  </span>
                </td>
                <td class="td-tos">
                  <span class="tos-badge" :class="getTermsClass(client)">
                    {{ getTermsLabel(client) }}
                  </span>
                </td>
              </tr>
              <!-- Expanded Row -->
              <tr v-if="isExpanded(client.id)" class="expanded-row">
                <td colspan="11">
                  <div class="expanded-content">
                    <!-- Basic Info -->
                    <div class="detail-section">
                      <h4 class="detail-section-title">פרטים בסיסיים</h4>
                      <div class="detail-grid">
                        <div class="detail-item">
                          <span class="detail-label">שם</span>
                          <span class="detail-value">{{ formatValue(client.name) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">מק״ט</span>
                          <span class="detail-value">{{ formatValue(client.makat) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">סוכן</span>
                          <span class="detail-value">{{ formatValue(client.agent) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">טלפון</span>
                          <span class="detail-value">{{ formatValue(client.phone) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">כתובת</span>
                          <span class="detail-value">{{ formatValue(client.address) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">עיר</span>
                          <span class="detail-value">{{ formatValue(client.city) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">סטטוס</span>
                          <span class="detail-value">{{ formatValue(client.status) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">זכאי למבצע ראשון</span>
                          <select
                            class="first-order-select"
                            :class="{ active: client.first_order }"
                            :value="client.first_order ? 'true' : 'false'"
                            :disabled="isUpdatingFirstOrder(client.id)"
                            @change="requestFirstOrderChange(client, $event)"
                          >
                            <option value="true">זכאי</option>
                            <option value="false">לא זכאי</option>
                          </select>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">מרכז</span>
                          <span class="detail-value">{{ formatValue(client.merakez) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">מגזר</span>
                          <span class="detail-value">{{ formatValue(client.migzar) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Consents -->
                    <ClientConsentPanel :client="client" />

                    <!-- Financial Info -->
                    <div class="detail-section">
                      <h4 class="detail-section-title">מידע פיננסי</h4>
                      <div class="detail-grid">
                        <div class="detail-item">
                          <span class="detail-label">מחירון</span>
                          <span class="detail-value">{{ formatValue(client.price_list_id) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">צפייה במחירים</span>
                          <button
                            class="prices-toggle"
                            :class="{
                              active: client.prices,
                              updating: isUpdatingPrices(client.id),
                            }"
                            :disabled="isUpdatingPrices(client.id)"
                            @click.stop="togglePrices(client)"
                          >
                            <span v-if="isUpdatingPrices(client.id)" class="toggle-spinner"></span>
                            <span v-else
                              >{{ client.prices ? 'כן' : 'לא' }}
                              <span class="toggle-hint">(לחץ לשינוי)</span></span
                            >
                          </button>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">מע״מ</span>
                          <span class="detail-value">{{ formatValue(client.vat) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">תשלום</span>
                          <span class="detail-value">{{ formatValue(client.payment) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">סוג תשלום</span>
                          <span class="detail-value">{{ formatValue(client.payment_type) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">אובליגו</span>
                          <span class="detail-value">{{ formatValue(client.obligo) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">הזמנה מינימלית (לפני מע״מ)</span>
                          <div class="min-order-edit" @click.stop>
                            <input
                              class="min-order-input"
                              type="number"
                              min="0"
                              step="1"
                              inputmode="numeric"
                              :value="minOrderInputValue(client)"
                              :disabled="isUpdatingMinOrder(client.id)"
                              @input="onMinOrderInput(client, $event)"
                              @keyup.enter="saveMinOrder(client)"
                            />
                            <span class="min-order-currency">₪</span>
                            <button
                              v-if="isMinOrderDirty(client)"
                              class="min-order-save"
                              :disabled="isUpdatingMinOrder(client.id)"
                              @click.stop="saveMinOrder(client)"
                            >
                              <span
                                v-if="isUpdatingMinOrder(client.id)"
                                class="toggle-spinner"
                              ></span>
                              <span v-else>שמור</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Notes & Metadata -->
                    <div class="detail-section">
                      <h4 class="detail-section-title">הערות ומידע נוסף</h4>
                      <div class="detail-grid">
                        <div class="detail-item detail-item-wide">
                          <span class="detail-label">הערות לקוח</span>
                          <span class="detail-value">{{ formatValue(client.client_notes) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">עדכון אחרון</span>
                          <span class="detail-value">{{ formatDateTime(client.last_update) }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">מוצרים מיוחדים</span>
                          <span class="detail-value">{{
                            formatValue(client.products_special)
                          }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Client Products -->
                    <div
                      v-if="client.client_products && client.client_products.length > 0"
                      class="detail-section"
                    >
                      <h4 class="detail-section-title">
                        מוצרי לקוח ({{ client.client_products.length }})
                      </h4>
                      <div class="client-products-scroll">
                        <div class="client-products-list">
                          <div
                            v-for="(product, idx) in client.client_products"
                            :key="idx"
                            class="product-item"
                          >
                            <span class="product-makat">{{ product.makat || '—' }}</span>
                            <span class="product-name">{{ product.name || '—' }}</span>
                            <svg
                              v-if="product.valid !== null"
                              class="product-invalid-icon"
                              viewBox="0 0 24 24"
                              width="16"
                              height="16"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <line x1="15" y1="9" x2="9" y2="15" />
                              <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Visitation Calendar -->
                    <div v-if="parseVisitation(client.visitation)" class="detail-section">
                      <h4 class="detail-section-title">לוח ביקורים</h4>
                      <div class="visitation-calendar">
                        <div class="calendar-header">
                          <span
                            v-for="(day, idx) in hebrewDays"
                            :key="idx"
                            class="calendar-day-label"
                            >{{ day }}</span
                          >
                        </div>
                        <div v-for="week in 4" :key="week" class="calendar-week">
                          <span class="week-label">שבוע {{ week }}</span>
                          <div class="week-days">
                            <span
                              v-for="day in 7"
                              :key="day"
                              class="calendar-cell"
                              :class="{
                                active:
                                  parseVisitation(client.visitation).dayNum === day &&
                                  parseVisitation(client.visitation).weeks[week - 1],
                                'is-day': parseVisitation(client.visitation).dayNum === day,
                              }"
                            >
                              <span
                                v-if="
                                  parseVisitation(client.visitation).dayNum === day &&
                                  parseVisitation(client.visitation).weeks[week - 1]
                                "
                                class="visit-dot"
                              ></span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Cards (Mobile) -->
      <div
        v-if="!isLoading && !error && filteredClients.length > 0"
        class="cards-container mobile-only"
      >
        <div
          v-for="client in paginatedClients"
          :key="client.id"
          class="client-card"
          :class="{ expanded: isExpanded(client.id) }"
        >
          <div class="card-header" @click="toggleExpand(client.id)">
            <div class="card-main">
              <span class="card-name">{{ formatValue(client.name) }}</span>
              <span class="card-makat">{{ formatValue(client.makat) }}</span>
            </div>
            <div class="card-badges">
              <span class="status-badge" :class="getClientStatusClass(client.status)">
                {{ formatValue(client.status) }}
              </span>
              <span v-if="isClientLinked(client.makat)" class="linked-badge linked">מקושר</span>
              <span class="tos-badge" :class="getTosClass(client)">
                {{ getTosLabel(client) }}
              </span>
            </div>
            <div class="card-actions">
              <button
                class="fullscreen-btn"
                @click.stop="openClientModal(client)"
                title="תצוגה מלאה"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"
                  />
                </svg>
              </button>
              <svg
                class="card-chevron"
                :class="{ expanded: isExpanded(client.id) }"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </div>

          <Transition name="card-expand">
            <div v-if="isExpanded(client.id)" class="card-details">
              <div class="card-detail-row">
                <span class="card-detail-label">סוכן</span>
                <span class="card-detail-value">{{ formatValue(client.agent) }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">טלפון</span>
                <span class="card-detail-value">{{ formatValue(client.phone) }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">עיר</span>
                <span class="card-detail-value">{{ formatValue(client.city) }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">יום ביקור</span>
                <span class="card-detail-value">{{ client.visitation?.DAYNAME || '—' }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">כתובת</span>
                <span class="card-detail-value">{{ formatValue(client.address) }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">זכאי למבצע ראשון</span>
                <select
                  class="first-order-select"
                  :class="{ active: client.first_order }"
                  :value="client.first_order ? 'true' : 'false'"
                  :disabled="isUpdatingFirstOrder(client.id)"
                  @change="requestFirstOrderChange(client, $event)"
                >
                  <option value="true">זכאי</option>
                  <option value="false">לא זכאי</option>
                </select>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">מחירון</span>
                <span class="card-detail-value">{{ formatValue(client.price_list_id) }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">תשלום</span>
                <span class="card-detail-value">{{ formatValue(client.payment) }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">מע״מ</span>
                <span class="card-detail-value">{{ formatValue(client.vat) }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">אובליגו</span>
                <span class="card-detail-value">{{ formatValue(client.obligo) }}</span>
              </div>
              <div class="card-detail-row">
                <span class="card-detail-label">עדכון אחרון</span>
                <span class="card-detail-value">{{ formatDateTime(client.last_update) }}</span>
              </div>
              <div v-if="client.client_notes" class="card-detail-row">
                <span class="card-detail-label">הערות</span>
                <span class="card-detail-value">{{ formatValue(client.client_notes) }}</span>
              </div>

              <!-- Visitation Calendar (Mobile) -->
              <div v-if="parseVisitation(client.visitation)" class="card-visitation">
                <span class="card-detail-label">לוח ביקורים</span>
                <div class="visitation-mini">
                  <div v-for="week in 4" :key="week" class="mini-week">
                    <span class="mini-week-label">ש{{ week }}</span>
                    <span
                      class="mini-cell"
                      :class="{ active: parseVisitation(client.visitation).weeks[week - 1] }"
                    ></span>
                  </div>
                  <span class="mini-day-info"
                    >יום {{ parseVisitation(client.visitation).dayNum }}</span
                  >
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>

    <!-- Fullscreen Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="modalClient" class="client-modal-overlay" @click="closeClientModal">
          <div class="client-modal" @click.stop>
            <div class="modal-header">
              <h2 class="modal-title">{{ modalClient.name }}</h2>
              <span class="modal-makat">מק״ט: {{ modalClient.makat }}</span>
              <button class="modal-close-btn" @click="closeClientModal">
                <svg
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <!-- Basic Info -->
              <div class="detail-section">
                <h4 class="detail-section-title">פרטים בסיסיים</h4>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">שם</span>
                    <span class="detail-value">{{ formatValue(modalClient.name) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">מק״ט</span>
                    <span class="detail-value">{{ formatValue(modalClient.makat) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">סוכן</span>
                    <span class="detail-value">{{ formatValue(modalClient.agent) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">טלפון</span>
                    <span class="detail-value">{{ formatValue(modalClient.phone) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">כתובת</span>
                    <span class="detail-value">{{ formatValue(modalClient.address) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">עיר</span>
                    <span class="detail-value">{{ formatValue(modalClient.city) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">סטטוס</span>
                    <span class="detail-value">{{ formatValue(modalClient.status) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">זכאי למבצע ראשון</span>
                    <select
                      class="first-order-select"
                      :class="{ active: modalClient.first_order }"
                      :value="modalClient.first_order ? 'true' : 'false'"
                      :disabled="isUpdatingFirstOrder(modalClient.id)"
                      @change="requestFirstOrderChange(modalClient, $event)"
                    >
                      <option value="true">זכאי</option>
                      <option value="false">לא זכאי</option>
                    </select>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">מרכז</span>
                    <span class="detail-value">{{ formatValue(modalClient.merakez) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">מגזר</span>
                    <span class="detail-value">{{ formatValue(modalClient.migzar) }}</span>
                  </div>
                </div>
              </div>

              <!-- Consents -->
              <ClientConsentPanel :client="modalClient" />

              <!-- Financial Info -->
              <div class="detail-section">
                <h4 class="detail-section-title">מידע פיננסי</h4>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">מחירון</span>
                    <span class="detail-value">{{ formatValue(modalClient.price_list_id) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">צפייה במחירים</span>
                    <button
                      class="prices-toggle"
                      :class="{
                        active: modalClient.prices,
                        updating: isUpdatingPrices(modalClient.id),
                      }"
                      :disabled="isUpdatingPrices(modalClient.id)"
                      @click.stop="togglePrices(modalClient)"
                    >
                      <span v-if="isUpdatingPrices(modalClient.id)" class="toggle-spinner"></span>
                      <span v-else
                        >{{ modalClient.prices ? 'כן' : 'לא' }}
                        <span class="toggle-hint">(לחץ לשינוי)</span></span
                      >
                    </button>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">מע״מ</span>
                    <span class="detail-value">{{ formatValue(modalClient.vat) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">תשלום</span>
                    <span class="detail-value">{{ formatValue(modalClient.payment) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">סוג תשלום</span>
                    <span class="detail-value">{{ formatValue(modalClient.payment_type) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">אובליגו</span>
                    <span class="detail-value">{{ formatValue(modalClient.obligo) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">הזמנה מינימלית (לפני מע״מ)</span>
                    <div class="min-order-edit" @click.stop>
                      <input
                        class="min-order-input"
                        type="number"
                        min="0"
                        step="1"
                        inputmode="numeric"
                        :value="minOrderInputValue(modalClient)"
                        :disabled="isUpdatingMinOrder(modalClient.id)"
                        @input="onMinOrderInput(modalClient, $event)"
                        @keyup.enter="saveMinOrder(modalClient)"
                      />
                      <span class="min-order-currency">₪</span>
                      <button
                        v-if="isMinOrderDirty(modalClient)"
                        class="min-order-save"
                        :disabled="isUpdatingMinOrder(modalClient.id)"
                        @click.stop="saveMinOrder(modalClient)"
                      >
                        <span
                          v-if="isUpdatingMinOrder(modalClient.id)"
                          class="toggle-spinner"
                        ></span>
                        <span v-else>שמור</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes & Metadata -->
              <div class="detail-section">
                <h4 class="detail-section-title">הערות ומידע נוסף</h4>
                <div class="detail-grid">
                  <div class="detail-item detail-item-wide">
                    <span class="detail-label">הערות לקוח</span>
                    <span class="detail-value">{{ formatValue(modalClient.client_notes) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">עדכון אחרון</span>
                    <span class="detail-value">{{ formatDateTime(modalClient.last_update) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">מוצרים מיוחדים</span>
                    <span class="detail-value">{{
                      formatValue(modalClient.products_special)
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Client Products -->
              <div
                v-if="modalClient.client_products && modalClient.client_products.length > 0"
                class="detail-section"
              >
                <h4 class="detail-section-title">
                  מוצרי לקוח ({{ modalClient.client_products.length }})
                </h4>
                <div class="client-products-scroll modal-products-scroll">
                  <div class="client-products-list">
                    <div
                      v-for="(product, idx) in modalClient.client_products"
                      :key="idx"
                      class="product-item"
                    >
                      <span class="product-makat">{{ product.makat || '—' }}</span>
                      <span class="product-name">{{ product.name || '—' }}</span>
                      <svg
                        v-if="product.valid !== null"
                        class="product-invalid-icon"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Visitation Calendar -->
              <div v-if="parseVisitation(modalClient.visitation)" class="detail-section">
                <h4 class="detail-section-title">לוח ביקורים</h4>
                <div class="visitation-calendar">
                  <div class="calendar-header">
                    <span v-for="(day, idx) in hebrewDays" :key="idx" class="calendar-day-label">{{
                      day
                    }}</span>
                  </div>
                  <div v-for="week in 4" :key="week" class="calendar-week">
                    <span class="week-label">שבוע {{ week }}</span>
                    <div class="week-days">
                      <span
                        v-for="day in 7"
                        :key="day"
                        class="calendar-cell"
                        :class="{
                          active:
                            parseVisitation(modalClient.visitation).dayNum === day &&
                            parseVisitation(modalClient.visitation).weeks[week - 1],
                          'is-day': parseVisitation(modalClient.visitation).dayNum === day,
                        }"
                      >
                        <span
                          v-if="
                            parseVisitation(modalClient.visitation).dayNum === day &&
                            parseVisitation(modalClient.visitation).weeks[week - 1]
                          "
                          class="visit-dot"
                        ></span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- First Order Confirmation Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="firstOrderConfirm" class="fo-confirm-overlay" @click="cancelFirstOrderChange">
          <div class="fo-confirm-modal" @click.stop>
            <div class="fo-confirm-icon">
              <svg
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 class="fo-confirm-title">עדכון זכאות למבצע ראשון</h3>
            <p class="fo-confirm-message">
              האם לעדכן את הזכאות למבצע ראשון עבור
              <strong>{{ firstOrderConfirm.client.name }}</strong>
              ל-<strong>{{ firstOrderConfirm.newValue ? 'זכאי' : 'לא זכאי' }}</strong
              >?
            </p>
            <div class="fo-confirm-actions">
              <button class="fo-btn fo-btn-cancel" @click="cancelFirstOrderChange">ביטול</button>
              <button class="fo-btn fo-btn-confirm" @click="confirmFirstOrderChange">אישור</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Pagination Bar -->
    <div v-if="!isLoading && !error && filteredClients.length > 0" class="pagination-bar">
      <div class="page-size-selector">
        <label>שורות בעמוד:</label>
        <select v-model="pageSize" @change="resetPage">
          <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
      </div>
      <div class="pagination-info">
        עמוד {{ currentPage }} מתוך {{ totalPages }}
        <span class="pagination-total">({{ filteredClients.length }} לקוחות)</span>
      </div>
      <div class="pagination-controls">
        <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
        <button
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ============================================
   Page Layout
   ============================================ */
.admin-clients {
  padding: var(--spacing-md);
  max-width: 1400px;
  margin: 0 auto;
  height: calc(100vh - var(--header-height));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-header {
  padding: var(--spacing-md) 0;
  flex-shrink: 0;
}

.page-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.page-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* ============================================
   Actions Bar
   ============================================ */
.actions-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) 0;
  flex-shrink: 0;
}

.actions-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.actions-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.results-count {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.btn-filters-toggle {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-filters-toggle:hover {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.btn-filters-toggle.active {
  background: rgba(99, 102, 241, 0.1);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--color-primary);
  color: white;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  border-radius: var(--border-radius-full);
}

/* ============================================
   Filters Section
   ============================================ */
.filters-section {
  flex-shrink: 0;
  margin-bottom: var(--spacing-md);
}

.filters-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
}

.search-input-container {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 350px;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  padding-right: 40px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  transition: all var(--transition-fast);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-transform: uppercase;
}

.select-wrapper {
  position: relative;
}

.filter-select,
.filter-input {
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-primary);
  min-width: 120px;
  transition: all var(--transition-fast);
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.filter-input {
  width: 100px;
}

.btn-clear-filters {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-clear-filters:hover {
  background: var(--color-error);
  color: white;
}

/* Filters collapse transition */
.filters-collapse-enter-active,
.filters-collapse-leave-active {
  transition: all var(--transition-base);
  overflow: hidden;
}

.filters-collapse-enter-from,
.filters-collapse-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.filters-collapse-enter-to,
.filters-collapse-leave-from {
  opacity: 1;
  max-height: 200px;
}

/* ============================================
   Content Section
   ============================================ */
.content-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Loading / Error / Empty */
.loading-container,
.error-container,
.empty-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  color: var(--text-muted);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text,
.error-text,
.empty-text {
  font-size: var(--font-size-base);
  margin: 0;
}

.empty-hint {
  font-size: var(--font-size-sm);
  margin: 0;
}

/* ============================================
   Table (Desktop)
   ============================================ */
.table-container {
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.data-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--bg-secondary);
}

.data-table th {
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: right;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  font-size: 11px;
  text-transform: uppercase;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.data-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
}

.data-row {
  cursor: pointer;
  transition: background var(--transition-fast);
}

.data-row:hover {
  background: var(--bg-secondary);
}

.data-row.expanded {
  background: rgba(99, 102, 241, 0.05);
}

.th-expand,
.td-expand {
  width: 40px;
}

.th-visit-day,
.td-visit-day {
  width: 90px;
  text-align: center;
}

.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  color: var(--text-muted);
  transition: all var(--transition-fast);
}

.expand-btn:hover {
  background: var(--bg-tertiary);
  color: var(--color-primary);
}

.expand-btn svg {
  transition: transform var(--transition-fast);
}

.expand-btn svg.rotated {
  transform: rotate(180deg);
}

.cell-value {
  color: var(--text-secondary);
}

.cell-name {
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.cell-makat {
  font-family: monospace;
  font-size: 12px;
}

/* Status Badge */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
}

.status-active {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}

.status-inactive {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.status-other,
.status-unknown {
  background: var(--bg-tertiary);
  color: var(--text-muted);
}

/* Linked Badge */
.linked-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
}

.linked-badge.linked {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}

.linked-badge.not-linked {
  background: transparent;
  color: var(--text-muted);
}

/* TOS / Marketing Badge */
.tos-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
}

.tos-badge.tos-approved {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}

.tos-badge.tos-declined {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.tos-badge.tos-pending {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
}

/* ============================================
   Expanded Row
   ============================================ */
.expanded-row td {
  padding: 0;
  background: var(--bg-secondary);
}

.expanded-content {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.detail-section {
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
}

.detail-section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--border-color);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--spacing-md);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-item-wide {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-transform: uppercase;
}

.detail-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  word-break: break-word;
}

/* Prices Toggle */
.prices-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  padding: 4px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.prices-toggle:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.05);
}

.prices-toggle.active {
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
  color: #059669;
}

.prices-toggle.active:hover:not(:disabled) {
  background: rgba(16, 185, 129, 0.25);
  border-color: #059669;
}

.prices-toggle:disabled {
  cursor: wait;
  opacity: 0.7;
}

.prices-toggle .toggle-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.prices-toggle .toggle-hint {
  font-size: 10px;
  font-weight: var(--font-weight-normal);
  opacity: 0.7;
}

/* First Order Select */
.first-order-select {
  display: inline-flex;
  min-width: 90px;
  padding: 4px 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: left 8px center;
  padding-left: 24px;
}

.first-order-select:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.first-order-select.active {
  background-color: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
  color: #059669;
}

.first-order-select.active:hover:not(:disabled) {
  background-color: rgba(16, 185, 129, 0.25);
  border-color: #059669;
}

.first-order-select:disabled {
  cursor: wait;
  opacity: 0.7;
}

/* Min Order inline edit */
.min-order-edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.min-order-input {
  width: 90px;
  padding: 4px 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  text-align: center;
}

.min-order-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.min-order-input:disabled {
  cursor: wait;
  opacity: 0.7;
}

.min-order-currency {
  font-size: 12px;
  color: var(--text-muted);
}

.min-order-save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  padding: 4px 12px;
  border: none;
  border-radius: var(--border-radius);
  background: var(--color-primary);
  color: white;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
}

.min-order-save:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.min-order-save:disabled {
  cursor: wait;
  opacity: 0.7;
}

.min-order-save .toggle-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* First Order Confirmation Modal */
.fo-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.fo-confirm-modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg, 12px);
  padding: var(--spacing-xl, 24px);
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.fo-confirm-icon {
  color: var(--color-warning);
  margin-bottom: var(--spacing-md, 12px);
}

.fo-confirm-title {
  font-size: var(--font-size-lg, 18px);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-sm, 8px);
}

.fo-confirm-message {
  font-size: var(--font-size-sm, 14px);
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-lg, 20px);
  line-height: 1.6;
}

.fo-confirm-actions {
  display: flex;
  gap: var(--spacing-sm, 8px);
  justify-content: center;
}

.fo-btn {
  padding: 8px 24px;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm, 14px);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  border: none;
  transition: all var(--transition-fast);
}

.fo-btn-cancel {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.fo-btn-cancel:hover {
  background: var(--bg-tertiary);
}

.fo-btn-confirm {
  background: var(--color-primary);
  color: white;
}

.fo-btn-confirm:hover {
  background: var(--color-primary-hover);
}

/* Client Products */
.client-products-scroll {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
}

.client-products-list {
  display: flex;
  flex-direction: column;
}

.product-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  transition: background var(--transition-fast);
}

.product-item:last-child {
  border-bottom: none;
}

.product-item:hover {
  background: var(--bg-tertiary);
}

.product-makat {
  font-family: monospace;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  padding: 2px 8px;
  border-radius: var(--border-radius);
  flex-shrink: 0;
  min-width: 60px;
  text-align: center;
}

.product-name {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-invalid-icon {
  flex-shrink: 0;
  color: var(--color-error);
}

/* Scrollbar for products list */
.client-products-scroll::-webkit-scrollbar {
  width: 6px;
}

.client-products-scroll::-webkit-scrollbar-track {
  background: var(--bg-secondary);
}

.client-products-scroll::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 3px;
}

.client-products-scroll::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

/* ============================================
   Visitation Calendar
   ============================================ */
.visitation-calendar {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.calendar-header {
  display: flex;
  gap: 4px;
  padding-right: 60px;
}

.calendar-day-label {
  width: 32px;
  text-align: center;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
}

.calendar-week {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.week-label {
  width: 52px;
  font-size: 11px;
  color: var(--text-muted);
}

.week-days {
  display: flex;
  gap: 4px;
}

.calendar-cell {
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.calendar-cell.is-day {
  border-color: var(--color-primary);
  background: rgba(99, 102, 241, 0.05);
}

.calendar-cell.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.visit-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
}

/* ============================================
   Cards (Mobile)
   ============================================ */
.cards-container {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.client-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
  overflow: hidden;
  flex-shrink: 0;
}

.client-card.expanded {
  border-color: var(--color-primary);
}

.card-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  cursor: pointer;
}

.card-main {
  flex: 1;
  min-width: 0;
}

.card-name {
  display: block;
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-makat {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  font-family: monospace;
}

.card-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.card-chevron {
  color: var(--text-muted);
  transition: transform var(--transition-fast);
  flex-shrink: 0;
}

.card-chevron.expanded {
  transform: rotate(180deg);
}

.card-details {
  padding: 0 var(--spacing-md) var(--spacing-md);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.card-detail-row {
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--border-color);
}

.card-detail-row:last-child {
  border-bottom: none;
}

.card-detail-label {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.card-detail-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  text-align: left;
}

.card-visitation {
  padding-top: var(--spacing-sm);
}

.visitation-mini {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xs);
}

.mini-week {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.mini-week-label {
  font-size: 10px;
  color: var(--text-muted);
}

.mini-cell {
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
}

.mini-cell.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.mini-day-info {
  font-size: 11px;
  color: var(--text-muted);
  margin-right: auto;
}

/* Card expand transition */
.card-expand-enter-active,
.card-expand-leave-active {
  transition: all var(--transition-base);
  overflow: hidden;
}

.card-expand-enter-from,
.card-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.card-expand-enter-to,
.card-expand-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* ============================================
   Pagination Bar
   ============================================ */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  margin-top: var(--spacing-md);
  flex-shrink: 0;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.page-size-selector select {
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-primary);
}

.pagination-info {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.pagination-total {
  color: var(--text-muted);
}

.pagination-controls {
  display: flex;
  gap: var(--spacing-xs);
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.pagination-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ============================================
   Responsive
   ============================================ */
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .admin-clients {
    padding: var(--spacing-sm);
  }

  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: flex;
  }

  .page-header {
    padding: var(--spacing-sm) 0;
  }

  .page-title {
    font-size: var(--font-size-lg);
  }

  .actions-bar {
    padding: var(--spacing-sm) 0;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
    padding: var(--spacing-sm);
    border-radius: var(--border-radius);
  }

  .search-input-container {
    max-width: none;
  }

  .filter-group {
    width: 100%;
  }

  .filter-select,
  .filter-input {
    width: 100%;
  }

  .pagination-bar {
    flex-wrap: wrap;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm);
    border-radius: var(--border-radius);
    margin-top: var(--spacing-sm);
  }

  .page-size-selector {
    order: 2;
    flex: 1;
  }

  .pagination-info {
    order: 1;
    width: 100%;
    text-align: center;
  }

  .pagination-controls {
    order: 3;
  }
}

/* ============================================
   Custom Scrollbar
   ============================================ */
.table-container::-webkit-scrollbar,
.cards-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track,
.cards-container::-webkit-scrollbar-track {
  background: var(--bg-secondary);
}

.table-container::-webkit-scrollbar-thumb,
.cards-container::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover,
.cards-container::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

/* ============================================
   Expand Actions (table)
   ============================================ */
.expand-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fullscreen-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  color: var(--text-muted);
  transition: all var(--transition-fast);
}

.fullscreen-btn:hover {
  background: var(--bg-tertiary);
  color: var(--color-primary);
}

/* ============================================
   Card Actions (mobile)
   ============================================ */
.card-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.card-actions .fullscreen-btn {
  width: 32px;
  height: 32px;
}

/* ============================================
   Fullscreen Modal
   ============================================ */
.client-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md);
}

.client-modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  flex-shrink: 0;
}

.modal-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
  flex: 1;
}

.modal-makat {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  font-family: monospace;
}

.modal-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  color: var(--text-muted);
  transition: all var(--transition-fast);
}

.modal-close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--color-error);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.modal-products-scroll {
  max-height: 300px;
}

/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-active .client-modal,
.modal-fade-leave-active .client-modal {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .client-modal,
.modal-fade-leave-to .client-modal {
  opacity: 0;
  transform: scale(0.95) translateY(20px);
}

.modal-fade-enter-to,
.modal-fade-leave-from {
  opacity: 1;
}

.modal-fade-enter-to .client-modal,
.modal-fade-leave-from .client-modal {
  opacity: 1;
  transform: scale(1) translateY(0);
}

/* Modal body scrollbar */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: var(--bg-secondary);
}

.modal-body::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

/* Modal responsive */
@media (max-width: 768px) {
  .client-modal-overlay {
    padding: 0;
  }

  .client-modal {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .modal-header {
    padding: var(--spacing-md);
    flex-wrap: wrap;
  }

  .modal-title {
    font-size: var(--font-size-lg);
    width: 100%;
    order: 1;
  }

  .modal-makat {
    order: 2;
    flex: 1;
  }

  .modal-close-btn {
    order: 0;
    position: absolute;
    top: var(--spacing-sm);
    left: var(--spacing-sm);
  }

  .modal-body {
    padding: var(--spacing-md);
  }
}
</style>
